import {
  ConnectionState,
  type LocalParticipant,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  Room,
  RoomEvent,
  Track,
} from "livekit-client"
import { useCallback, useEffect, useRef, useState } from "react"
import { useConversationLogger } from "./useConversationLogger"

export interface LiveKitParticipant {
  identity: string
  isSpeaking: boolean
  isMuted: boolean
  isLocal: boolean
  participantIndex: number
}

export type DataMessageEvent =
  | {
      type: "restart:requested"
      restartId: string
      requiredVotes: number
    }
  | {
      type: "restart:voted"
      restartId: string
      userId: string
      vote: boolean
      yesCount: number
      noCount: number
    }
  | { type: "restart:approved"; restartId: string }
  | {
      type: "restart:executed"
      restartId: string
      recordingStartsAt?: string
    }
  | { type: "restart:rejected"; restartId: string }
  | { type: "countdown:start"; recordingStartsAt: string }

export interface UseLiveKitOptions {
  token: string
  serverUrl: string
  onDataMessage?: (event: DataMessageEvent) => void
}

export interface UseLiveKitReturn {
  connectionState: ConnectionState
  participants: LiveKitParticipant[]
  isMuted: boolean
  toggleMute: () => void
}

function parseParticipantIndex(p: LocalParticipant | RemoteParticipant): number {
  try {
    const meta = JSON.parse(p.metadata ?? "{}")
    return typeof meta.participantIndex === "number" ? meta.participantIndex : -1
  } catch {
    return -1
  }
}

function toParticipant(
  p: LocalParticipant | RemoteParticipant,
  isLocal: boolean,
): LiveKitParticipant {
  const audioTrack = p
    .getTrackPublications()
    .find((t) => t.source === Track.Source.Microphone)
  return {
    identity: p.identity,
    isSpeaking: p.isSpeaking,
    isMuted: audioTrack?.isMuted ?? true,
    isLocal,
    participantIndex: parseParticipantIndex(p),
  }
}

export function useLiveKit({
  token,
  serverUrl,
  onDataMessage,
}: UseLiveKitOptions): UseLiveKitReturn {
  const log = useConversationLogger()
  const roomRef = useRef<Room | null>(null)
  const onDataMessageRef = useRef(onDataMessage)
  onDataMessageRef.current = onDataMessage
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.Disconnected,
  )
  const [participants, setParticipants] = useState<LiveKitParticipant[]>([])
  const [isMuted, setIsMuted] = useState(false)
  const muteInFlightRef = useRef(false)

  const updateParticipants = useCallback((room: Room) => {
    const all: LiveKitParticipant[] = [
      toParticipant(room.localParticipant, true),
      ...[...room.remoteParticipants.values()].map((p) => toParticipant(p, false)),
    ].sort((a, b) => a.participantIndex - b.participantIndex)
    setParticipants(all)
  }, [])

  useEffect(() => {
    if (!token || !serverUrl) return

    let active = true
    const room = new Room({
      audioCaptureDefaults: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      publishDefaults: {
        dtx: true,
      },
    })
    roomRef.current = room

    log("livekit", `Connecting to ${serverUrl}`)

    const onConnectionStateChanged = (state: ConnectionState) => {
      if (!active) return
      log("livekit", `Connection state: ${state}`)
      setConnectionState(state)
    }

    const onParticipantConnected = (participant: RemoteParticipant) => {
      if (!active) return
      log("livekit", `Participant connected: ${participant.identity}`, {
        sid: participant.sid,
        identity: participant.identity,
      })
      updateParticipants(room)
    }

    const onParticipantDisconnected = (participant: RemoteParticipant) => {
      if (!active) return
      log("livekit", `Participant disconnected: ${participant.identity}`, {
        sid: participant.sid,
        identity: participant.identity,
      })
      updateParticipants(room)
    }

    const onActiveSpeakers = () => {
      if (!active) return
      const speakers = [
        room.localParticipant,
        ...room.remoteParticipants.values(),
      ]
        .filter((p) => p.isSpeaking)
        .map((p) => p.identity)
      if (speakers.length > 0) {
        log("livekit", `Active speakers: ${speakers.join(", ")}`)
      }
      updateParticipants(room)
    }

    const onTrackMuted = () => {
      if (!active) return
      log("livekit", "Track muted event")
      updateParticipants(room)
    }

    const onTrackUnmuted = () => {
      if (!active) return
      log("livekit", "Track unmuted event")
      updateParticipants(room)
    }

    const onLocalTrackPublished = () => {
      if (!active) return
      log("livekit", "Local track published")
      updateParticipants(room)
    }

    const audioElements: HTMLAudioElement[] = []

    const onTrackSubscribed = (
      track: RemoteTrack,
      publication: RemoteTrackPublication,
      participant: RemoteParticipant,
    ) => {
      if (track.source !== Track.Source.Microphone) return
      log(
        "livekit",
        `Attaching remote audio track from ${participant.identity}`,
        {
          trackSid: publication.trackSid,
        },
      )
      const el = track.attach()
      el.setAttribute("data-lk-participant", participant.identity)
      document.body.appendChild(el)
      audioElements.push(el)
    }

    const onTrackUnsubscribed = (
      track: RemoteTrack,
      _publication: RemoteTrackPublication,
      participant: RemoteParticipant,
    ) => {
      if (track.source !== Track.Source.Microphone) return
      log(
        "livekit",
        `Detaching remote audio track from ${participant.identity}`,
      )
      track.detach().forEach((el) => {
        const idx = audioElements.indexOf(el)
        if (idx !== -1) audioElements.splice(idx, 1)
        el.remove()
      })
    }

    const onDataReceived = (payload: Uint8Array) => {
      if (!active) return
      try {
        const text = new TextDecoder().decode(payload)
        const parsed = JSON.parse(text) as DataMessageEvent
        log("livekit", `Data message: ${parsed.type}`, parsed)
        onDataMessageRef.current?.(parsed)
      } catch {
        // Ignore non-JSON data messages
      }
    }

    room.on(RoomEvent.DataReceived, onDataReceived)
    room.on(RoomEvent.ConnectionStateChanged, onConnectionStateChanged)
    room.on(RoomEvent.ParticipantConnected, onParticipantConnected)
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected)
    room.on(RoomEvent.ActiveSpeakersChanged, onActiveSpeakers)
    room.on(RoomEvent.TrackMuted, onTrackMuted)
    room.on(RoomEvent.TrackUnmuted, onTrackUnmuted)
    room.on(RoomEvent.LocalTrackPublished, onLocalTrackPublished)
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed)
    room.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed)

    room
      .connect(serverUrl, token)
      .then(() => {
        if (!active) return
        log("livekit", "Connected to room, enabling microphone", {
          roomName: room.name,
          localIdentity: room.localParticipant.identity,
          remoteParticipants: room.remoteParticipants.size,
        })
        room.localParticipant.setMicrophoneEnabled(true).then(() => {
          if (!active) return
          log("mic", "Microphone enabled in LiveKit room")
          updateParticipants(room)
        }).catch(() => {})
      })
      .catch((err) => {
        if (!active) return
        log("error", `LiveKit connection failed: ${err.message}`, {
          error: String(err),
          serverUrl,
        })
        console.error("LiveKit connection failed:", err)
      })

    return () => {
      active = false
      log("livekit", "Disconnecting from room")
      for (const el of audioElements) el.remove()
      audioElements.length = 0
      room.disconnect()
      roomRef.current = null
    }
  }, [token, serverUrl, updateParticipants, log])

  const toggleMute = useCallback(() => {
    const room = roomRef.current
    if (!room || muteInFlightRef.current) return
    const next = !isMuted
    muteInFlightRef.current = true
    log("mic", next ? "Muting microphone" : "Unmuting microphone")
    room.localParticipant.setMicrophoneEnabled(!next).then(() => {
      setIsMuted(next)
      muteInFlightRef.current = false
      log("mic", `Microphone ${next ? "muted" : "unmuted"}`)
      updateParticipants(room)
    }).catch(() => { muteInFlightRef.current = false })
  }, [isMuted, updateParticipants, log])

  return { connectionState, participants, isMuted, toggleMute }
}
